// ─────────────────────────────────────────────────────────────────────────────
// Centralized module registry — single source of truth for:
//   • module ID + label (used in user-management UI)
//   • module → admin_path / manager_path (used in routing, sidebar, redirects)
//   • role → default modules (used in user create flow)
//   • role → preferred home path (used in post-login landing)
//   • tenant feature flag mapping (used in route gating + sidebar)
//
// If you add a new module, add it HERE only — App.js routes, ProtectedRoute,
// Layout sidebar, and AdminManagers user-creation form all consume this file.
// ─────────────────────────────────────────────────────────────────────────────
import { CHILD_TO_PARENT } from './feature_parents';

/**
 * MODULES is the canonical list. Each entry maps a backend module id to:
 *   - label:   human label shown in user-management chips
 *   - group:   used for grouping in pickers
 *   - admin_path / manager_path: target route per role family
 *   - feature: optional tenant feature flag (matches backend MODULE_TO_FEATURE)
 *
 * Routes that don't correspond to a backend module (e.g. /admin/outlets,
 * /admin/managers, /admin/portfolios) are admin-only and not gated by module.
 */
export const MODULES = [
  // Daily ops
  { id: 'daily_entry',         label: 'Daily Entry',         group: 'Daily Ops',
    admin_path: null,                       manager_path: '/manager/entry' },
  { id: 'store_issues',        label: 'Store Issues',        group: 'Daily Ops',
    admin_path: null,                       manager_path: '/manager/entry' },
  { id: 'kitchen',             label: 'Kitchen Closing',     group: 'Daily Ops',
    admin_path: null,                       manager_path: '/manager/kitchen' },
  { id: 'bar',                 label: 'Bar Closing',         group: 'Daily Ops',
    admin_path: null,                       manager_path: '/manager/bar' },

  // Recipe / pricing / production / inventory / insights
  { id: 'recipes',             label: 'Recipes',             group: 'Recipe Studio',
    admin_path: '/admin/recipes',           manager_path: '/manager/recipes',
    feature: 'recipe_module' },
  { id: 'pricing',             label: 'Pricing',             group: 'Recipe Studio',
    admin_path: '/admin/recipes',           manager_path: '/manager/recipes',
    feature: 'pricing_module' },
  { id: 'production',          label: 'Production Planner',  group: 'Recipe Studio',
    admin_path: '/admin/production',        manager_path: '/manager/production',
    feature: 'production_planner' },
  { id: 'inventory',           label: 'Inventory',           group: 'Inventory',
    admin_path: '/admin/inventory',         manager_path: '/manager/kitchen',
    feature: 'inventory_module' },
  { id: 'cost_insights',       label: 'Cost Insights',       group: 'Reports',
    admin_path: '/admin/cost-insights',     manager_path: '/manager/cost-insights',
    feature: 'cost_insights' },

  // Financial / customer / ops
  { id: 'pnl',                 label: 'P&L',                 group: 'Reports',
    admin_path: '/admin/pnl',               manager_path: '/manager/pnl' },
  { id: 'customer_experience', label: 'Customer Experience', group: 'Customer',
    admin_path: '/admin/customer-experience', manager_path: '/manager/customer-feedback' },
  { id: 'reports',             label: 'Insights',            group: 'Reports',
    admin_path: '/admin/insights',          manager_path: '/manager/insights' },
  { id: 'marketing',           label: 'Marketing',           group: 'Ops',
    admin_path: '/admin/marketing',         manager_path: '/manager/marketing' },
  { id: 'maintenance',         label: 'Maintenance',         group: 'Ops',
    admin_path: '/admin/maintenance',       manager_path: '/manager/maintenance' },
  { id: 'checklist',           label: 'Checklist',           group: 'Ops',
    admin_path: '/admin/checklist',         manager_path: '/manager/checklist' },
  { id: 'reservations',        label: 'Reservations',        group: 'Ops',
    admin_path: '/admin/reservations',      manager_path: '/manager/reservations' },

  // Financial ops
  { id: 'petty_cash',          label: 'Petty Cash',          group: 'Finance',
    admin_path: '/admin/petty-cash',        manager_path: '/manager/petty-cash' },

  // ITER293 — Loyalty program (assignment-gated on both shells).
  { id: 'loyalty',             label: 'Loyalty Program',     group: 'Customer',
    admin_path: '/admin/loyalty',           manager_path: '/manager/loyalty',
    feature: 'loyalty' },
  // ITER293 — Customer CRM (admin-only surface).
  { id: 'customers',           label: 'Customers (CRM)',     group: 'Customer',
    admin_path: '/admin/customers',         manager_path: null,
    feature: 'crm' },
  // ITER293 — Action / Communication Center (both shells).
  { id: 'communication',       label: 'Communications',      group: 'Ops',
    admin_path: '/admin/action-comm',       manager_path: '/manager/action-comm',
    feature: 'communication' },
  // ITER293 — Menu Management. Admin side lives under /admin/outlets/:id/menus
  // (which is admin-only anyway, no extra gate needed); manager side is the
  // 86'd-items availability screen.
  { id: 'menus',               label: 'Menu Management',     group: 'Ops',
    admin_path: null,                       manager_path: '/manager/menu-availability' },

  // ITER295 — Operations Excellence · Smart Ticketing + Failure Register.
  { id: 'tickets',             label: 'Operational Tickets', group: 'Ops',
    admin_path: '/admin/tickets',           manager_path: '/manager/tickets' },

  // ITER296 — Ops Excellence · Command Centre + Compliance Intelligence + Notifications.
  { id: 'ops_command',           label: 'Ops Command Centre',    group: 'Ops',
    admin_path: '/admin/ops-command',       manager_path: '/manager/ops-command' },
  { id: 'compliance_intelligence', label: 'Compliance Intelligence', group: 'Ops',
    admin_path: '/admin/compliance-intelligence', manager_path: '/manager/compliance-intelligence' },
  { id: 'notification_rules',    label: 'Notification Rules',    group: 'Ops',
    admin_path: '/admin/notification-rules', manager_path: null },
  // ITER296.2 — Ticket Settings (routing table + priority SLA).
  { id: 'ticket_settings',       label: 'Ticket Settings',       group: 'Ops',
    admin_path: '/admin/ticket-settings',    manager_path: null },

  // iter165 — Customer Pulse Index (premium hospitality pulse engine)
  { id: 'cx_forms',            label: 'Customer Pulse',      group: 'Ops',
    admin_path: '/admin/cx',                manager_path: null,
    feature_flag: 'custom_feedback_forms' },

  // Platform
  { id: 'settings',            label: 'Settings',            group: 'Platform',
    admin_path: '/admin/settings',          manager_path: null },

  // POS Sprint 1 · Commerce · Terminal + Staff Settings. Both routes are
  // gated on the `pos` tenant feature and rendered outside the standard
  // admin/manager shells (see /app/frontend/src/App.js). The `admin_path`
  // is what modulesForPath() consumes when resolving a route → module id;
  // both entries carry the same feature key so a single `pos` toggle in
  // Super Admin controls navbar visibility and route access together.
  { id: 'pos_terminal',        label: 'POS Terminal',        group: 'Commerce',
    admin_path: '/pos',                     manager_path: '/pos',
    feature: 'pos' },
  { id: 'pos_settings',        label: 'POS Settings',        group: 'Commerce',
    admin_path: '/pos/settings',            manager_path: null,
    feature: 'pos' },
];

/** Quick id → entry lookup. */
const _byId = MODULES.reduce((m, x) => { m[x.id] = x; return m; }, {});
export const MODULE_BY_ID = _byId;

/**
 * Some routes are entry points for MULTIPLE modules — e.g. `/manager/entry`
 * is the home for both `daily_entry` and `store_issues`; `/manager/kitchen`
 * carries both `kitchen` (closing) and `inventory` (store input). When gating
 * these paths we accept ANY of these as a valid access ticket.
 */
const PATH_MODULE_ALIASES = {
  '/manager/entry':    ['daily_entry', 'store_issues'],
  '/manager/history':  ['daily_entry'],  // ITER293 — personal daily-entry history
  '/manager/kitchen':  ['kitchen', 'inventory'],
  '/manager/bar':      ['bar', 'inventory'],
  '/manager/recipes':  ['recipes', 'pricing'],
  '/admin/inventory':  ['inventory'],
  '/admin/opening-stock': ['inventory'],
};

/** Used in the user-management module-picker UI. */
export const MODULE_OPTIONS = MODULES.map(m => ({ id: m.id, label: m.label, group: m.group }));

/**
 * Role → default module set assigned when admin creates a user.
 * Empty = role has implicit org-wide / outlet-wide access (handled by RBAC).
 */
export const ROLE_DEFAULT_MODULES = {
  admin: [],
  area_manager: [],
  manager: [],              // empty = legacy full outlet access
  supervisor: ['daily_entry'],
  production_head: ['recipes', 'production'],
  // ITER308-B · maintenance_manager — scoped admin. Sidebar + admin
  // routes are driven entirely by assigned_modules; default seed is
  // maintenance (covers assets, PM, inspections, service history).
  maintenance_manager: ['maintenance'],
  chef: ['recipes', 'production', 'kitchen'],
  accountant: ['pnl', 'reports', 'petty_cash'],
  store_manager: ['store_issues', 'inventory'],
  cashier: ['daily_entry'],
  maintenance: ['maintenance', 'checklist'],
};

/** Role families. */
export const ADMIN_LIKE_ROLES = ['admin', 'area_manager', 'super_admin'];
export const MANAGER_LIKE_ROLES = ['manager', 'supervisor', 'chef', 'accountant', 'store_manager', 'cashier', 'maintenance'];
// ITER308-B · Scoped-admin roles route to the admin shell but see ONLY
// their `assigned_modules`. This is now the single source of truth —
// App.js + Layout.js both derive from this list, no hardcoded whitelist.
export const SCOPED_ADMIN_ROLES = ['production_head', 'maintenance_manager'];
export const ALL_NON_STUDIO_ROLES = [...ADMIN_LIKE_ROLES, ...MANAGER_LIKE_ROLES, ...SCOPED_ADMIN_ROLES];

/**
 * UI mode resolver — single source of truth for the "what UI do I render?" question.
 *
 * The same module (e.g. petty_cash, inventory, kitchen) can present TWO different
 * interfaces depending on the viewer:
 *
 *   • admin_view    → control / review / approve / audit / analytics
 *   • manager_view  → execute / enter / submit / operate
 *
 * Modules use this to switch between layouts:
 *
 *   import { resolveModuleView } from '../config/modules';
 *   const view = resolveModuleView(user);   // 'admin_view' | 'manager_view'
 *
 * Production heads occupy an admin-like analytical seat (they review recipe &
 * production data across outlets) but execute nothing themselves — they get
 * the admin view. Super admins always get admin view.
 */
export function resolveModuleView(user) {
  if (!user) return 'manager_view';
  // ITER308-B · production_head + maintenance_manager use the admin
  // shell (analytical / control seat), not the manager execution shell.
  if (ADMIN_LIKE_ROLES.includes(user.role)
      || SCOPED_ADMIN_ROLES.includes(user.role)) return 'admin_view';
  if (MANAGER_LIKE_ROLES.includes(user.role)) return 'manager_view';
  return 'manager_view';
}

/** True when the user is an operational executor (manager-shell consumer). */
export const isOperationalUser = (user) => resolveModuleView(user) === 'manager_view';

/** True when the user is in control/review mode. */
export const isAdminUser = (user) => resolveModuleView(user) === 'admin_view';

/**
 * Resolve a path → list of backend module ids that grant access.
 *
 * Returns:
 *   - []           → path is not module-gated (admin pass-through)
 *   - [id, ...]    → user must have AT LEAST ONE of these modules to access
 */
export function modulesForPath(path) {
  if (!path) return [];
  // Check aliases first (longest-prefix match)
  const aliasKey = Object.keys(PATH_MODULE_ALIASES)
    .sort((a, b) => b.length - a.length)
    .find(k => path.startsWith(k));
  if (aliasKey) return PATH_MODULE_ALIASES[aliasKey];
  // Then walk the canonical list
  for (const m of MODULES) {
    if (m.admin_path && path.startsWith(m.admin_path)) return [m.id];
    if (m.manager_path && path.startsWith(m.manager_path)) return [m.id];
  }
  return [];
}

/**
 * Back-compat helper used by ProtectedRoute. Returns the FIRST module id
 * the path is gated by, or null if not gated. Use modulesForPath() for the
 * full set when validating access.
 */
export function moduleForPath(path) {
  const ids = modulesForPath(path);
  return ids.length > 0 ? ids[0] : null;
}

/**
 * Returns true if the path is accessible to the given user.
 *
 *   - Admin-like roles bypass module gating entirely.
 *   - Legacy managers (no assigned_modules) bypass too.
 *   - Otherwise: user must have at least one of the path's gating modules.
 */
export function userCanAccessPath(user, path) {
  if (!user || !path) return false;
  if (ADMIN_LIKE_ROLES.includes(user.role)) return true;
  const ids = modulesForPath(path);
  if (ids.length === 0) {
    // Not module-gated. Admin-only pages still require admin role above; for
    // manager-shell pages we conservatively allow.
    return true;
  }
  const mods = Array.isArray(user.assigned_modules) ? user.assigned_modules : [];
  if (user.role === 'manager' && mods.length === 0) return true;  // legacy full access
  return ids.some(id => mods.includes(id));
}

/**
 * iter177 — Tenant landing priority chain.
 *
 * When a tenant has Dashboard disabled, redirect to the first enabled module
 * in this priority order (matches the operational hierarchy user requested):
 *   Dashboard → Financials (P&L) → Customers → Operations → Communication → Organization
 *
 * Each entry: { path, feature }. `feature: null` means always available.
 */
export const ADMIN_LANDING_PRIORITY = [
  { path: '/admin/dashboard',            feature: 'dashboard' },
  { path: '/admin/insights',             feature: 'financial_dashboard' },
  { path: '/admin/pnl',                  feature: 'pnl_live' },
  { path: '/admin/cost-insights',        feature: 'cost_insights' },
  { path: '/admin/customers',            feature: 'crm' },
  { path: '/admin/customer-experience',  feature: 'customer_feedback' },
  { path: '/admin/cx',                   feature: 'custom_feedback_forms' },
  { path: '/admin/loyalty',              feature: 'loyalty' },
  { path: '/admin/checklist',            feature: 'checklist' },
  { path: '/admin/inventory',            feature: 'inventory_module' },
  { path: '/admin/recipes',              feature: 'recipe_module' },
  { path: '/admin/petty-cash',           feature: 'petty_cash' },
  { path: '/admin/action-comm',          feature: 'communication' },
  { path: '/admin/marketing',            feature: 'marketing' },
  { path: '/admin/managers',             feature: 'user_management' },
  { path: '/admin/outlets',              feature: 'outlet_management' },
  // sprint 376 — Review & Discussion tenant-assignable module. Standalone
  // sellable so it's included in the landing chain — a tenant with ONLY
  // review_discussion ON lands here directly.
  { path: '/admin/reviews',              feature: 'review_discussion' },
  // iter183k — Expansion Intelligence: eligible as a landing module ONLY when
  // a super-admin explicitly assigns it (Q5 lock-in: never auto-assigned on
  // enable). The entry below makes it reachable through pickLandingPath when
  // chosen; default-priority resolution never lands here automatically because
  // every operational module above is `default: true` and EI defaults to OFF.
  { path: '/admin/expansion-intelligence', feature: 'expansion_intelligence' },
  // iter178 — Settings intentionally NOT in landing chain. Settings is a
  // meta page (profile/preferences), not a module. When every business
  // feature is disabled, RootRedirect must surface RestrictedHome instead
  // of bouncing the user into Settings.
];

const MANAGER_LANDING_PRIORITY = [
  { path: '/manager/overview',           feature: 'dashboard' },  // Overview (Feb 2026)
  { path: '/manager/entry',              feature: null },  // Daily Business Report (core)
  { path: '/manager/pnl',                feature: 'pnl_live' },
  { path: '/manager/kitchen',            feature: 'kitchen_closing' },
  { path: '/manager/bar',                feature: 'bar_closing' },
  { path: '/manager/cost-insights',      feature: 'cost_insights' },
  { path: '/manager/checklist',          feature: 'checklist' },
  // ITER293 — Include the remaining manager-shell modules so supervisors /
  // specialists with narrower assignments (e.g. marketing-only) still get a
  // welcome card that lands them on a module they actually own.
  { path: '/manager/marketing',          feature: 'marketing' },
  { path: '/manager/reservations',       feature: 'reservations_module' },
  { path: '/manager/customer-feedback',  feature: 'customer_feedback' },
  { path: '/manager/loyalty',            feature: 'loyalty' },
  { path: '/manager/action-comm',        feature: 'communication' },
  { path: '/manager/recipes',            feature: 'recipe_module' },
  { path: '/manager/production',         feature: 'production_planner' },
  { path: '/manager/petty-cash',         feature: 'petty_cash' },
  { path: '/manager/maintenance',        feature: 'maintenance' },
  { path: '/manager/insights',           feature: 'reports' },
  // sprint 376 — Review & Discussion standalone-sellable module (manager).
  { path: '/manager/reviews',            feature: 'review_discussion' },
];

/** Is a tenant feature enabled? Mirrors Layout.js semantics — `undefined`
 * means "no value loaded yet" → treat as enabled (default-on).
 * iter180 — Also enforces parent cascade: child is OFF if its parent is OFF.  */
function isFeatureOn(tenantFeatures, feature) {
  if (!feature) return true;
  if (!tenantFeatures) return true;
  if (tenantFeatures[feature] === false) return false;
  const parent = CHILD_TO_PARENT[feature];
  if (parent && tenantFeatures[parent] === false) return false;
  return true;
}

/**
 * iter177 — Find the first enabled landing path for a role given the tenant's
 * enabled feature map. Returns null if NOTHING is reachable (caller renders
 * RestrictedHome).
 */
export function pickFeatureAwareLandingPath(role, tenantFeatures) {
  const chain = role === 'manager' ? MANAGER_LANDING_PRIORITY : ADMIN_LANDING_PRIORITY;
  for (const entry of chain) {
    if (isFeatureOn(tenantFeatures, entry.feature)) return entry.path;
  }
  return null;
}

/**
 * iter177 — All enabled admin destinations for the tenant (used by
 * RestrictedHome to render a card grid of accessible modules). Returns
 * `[{ path, feature, label }]` in priority order, skipping disabled features.
 *
 * ITER293 — Also filters by the caller's assigned_modules so we never
 * surface cards a supervisor / non-admin user can't actually open. When
 * `user` is omitted the raw tenant-enabled list is returned (used by
 * legacy paths that pre-date module gating).
 */
export function listEnabledLandings(role, tenantFeatures, user = null) {
  // ITER293 — Manager-shell roles (manager, supervisor, chef, etc.) use the
  // manager landing chain so the welcome grid never surfaces admin-only
  // cards (dashboard, portfolios, managers) that they cannot reach.
  const chain = MANAGER_LIKE_ROLES.includes(role) ? MANAGER_LANDING_PRIORITY : ADMIN_LANDING_PRIORITY;
  return chain
    .filter((e) => isFeatureOn(tenantFeatures, e.feature))
    .filter((e) => (user ? userCanAccessPath(user, e.path) : true))
    .map((e) => ({ ...e, label: _labelFor(e.path) }));
}

const _LANDING_LABELS = {
  '/admin/dashboard': 'Dashboard',
  '/admin/pnl': 'P&L',
  '/admin/cost-insights': 'Cost Insights',
  '/admin/insights': 'Insights & Reports',
  '/admin/customers': 'Customers (CRM)',
  '/admin/customer-experience': 'Customer Experience',
  '/admin/cx': 'Customer Pulse',
  '/admin/loyalty': 'Loyalty',
  '/admin/checklist': 'Checklist',
  '/admin/inventory': 'Inventory',
  '/admin/recipes': 'Recipes',
  '/admin/petty-cash': 'Petty Cash',
  '/admin/action-comm': 'Communication',
  '/admin/marketing': 'Marketing',
  '/admin/managers': 'Users',
  '/admin/outlets': 'Outlets',
  '/admin/settings': 'Settings',
  '/manager/overview': 'Overview',
  '/manager/entry': 'Daily Business Report',
  '/manager/pnl': 'P&L',
  '/manager/kitchen': 'Kitchen Closing',
  '/manager/bar': 'Bar Closing',
  '/manager/cost-insights': 'Cost Insights',
  '/manager/checklist': 'Checklist',
  // ITER293 — Manager-shell landing labels for the expanded chain.
  '/manager/marketing': 'Marketing',
  '/manager/reservations': 'Reservations',
  '/manager/customer-feedback': 'Customer Feedback',
  '/manager/loyalty': 'Loyalty',
  '/manager/action-comm': 'Communications',
  '/manager/recipes': 'Recipes',
  '/manager/production': 'Production',
  '/manager/petty-cash': 'Petty Cash',
  '/manager/maintenance': 'Maintenance',
  '/manager/insights': 'Insights & Reports',
};
function _labelFor(path) {
  return _LANDING_LABELS[path] || path.split('/').pop().replace(/-/g, ' ');
}

/**
 * Pick the safest landing path for a logged-in user.
 *
 * The key invariant: the returned path MUST be a route the user can actually
 * access. We iterate through every module the user has, and for each candidate
 * manager/admin path we verify `userCanAccessPath()` returns true before
 * committing. This avoids the "module not assigned" bounce that happens when
 * a user's first module's manager_path is gated by a DIFFERENT module they
 * don't have (e.g. store_issues → /manager/entry which gates daily_entry).
 *
 * Returns null when nothing safe is available — caller MUST handle this so the
 * user never sees a blank page.
 */
export function pickLandingPath(user, tenantFeatures) {
  if (!user) return '/login';
  const role = user.role;
  if (role === 'studio_chef') return '/studio/home';
  if (role === 'super_admin') return '/super/dashboard';
  // Studio users are a dedicated consultant product — they land directly
  // on the Expansion Intelligence workspace and have no other navigation.
  if (role === 'studio_user') return '/admin/expansion-intelligence';
  // iter177 — admin / area_manager pick from a feature-aware priority chain
  // so a tenant with Dashboard disabled lands on the next enabled module
  // instead of an empty "Feature not enabled" screen.
  if (role === 'admin' || role === 'area_manager') {
    const featureAware = pickFeatureAwareLandingPath(role, tenantFeatures);
    if (featureAware) return featureAware;
    // Hard fallback — legacy behaviour when tenantFeatures isn't loaded yet.
    return tenantFeatures === undefined ? '/admin/dashboard' : null;
  }

  const mods = Array.isArray(user.assigned_modules) ? user.assigned_modules : [];

  // iter177 — manager: feature-aware landing first, then module fallback.
  if (role === 'manager') {
    if (mods.length === 0) {
      const featureAware = pickFeatureAwareLandingPath('manager', tenantFeatures);
      return featureAware || '/manager/entry';
    }
  }

  // Build a stub user we can pass to userCanAccessPath().
  const stub = { role, assigned_modules: mods };

  if (mods.length > 0) {
    // Prefer manager_path first, fall back to admin_path. Validate that the
    // candidate path is actually accessible before committing.
    // ITER308-B · Scoped-admin roles (production_head + maintenance_manager)
    // invert the preference — they live in the admin shell so admin_path
    // wins.
    const isScopedAdmin = role === 'production_head' || role === 'maintenance_manager';
    for (const id of mods) {
      const m = _byId[id];
      if (!m) continue;
      const candidates = isScopedAdmin
        ? [m.admin_path, m.manager_path]
        : [m.manager_path, m.admin_path];
      for (const path of candidates) {
        if (path && userCanAccessPath(stub, path)) return path;
      }
    }
  }

  if (role === 'production_head') return '/admin/recipes';
  if (role === 'maintenance_manager') return '/admin/maintenance';

  return null;  // caller renders <NoAccessScreen>
}

/**
 * Given a role and module id, pick the route to redirect to when the user tries
 * to access a module they don't have. Used by ProtectedRoute fallback.
 */
export function pickFallbackForModule(role, moduleId) {
  const m = _byId[moduleId];
  if (!m) return null;
  if (ADMIN_LIKE_ROLES.includes(role) && m.admin_path) return m.admin_path;
  if (m.manager_path) return m.manager_path;
  return m.admin_path || null;
}


/* ─────────────── Capability resolver (iter163) ───────────────
 * Mirrors /app/backend/services/permissions.py exactly. The frontend
 * MUST hit `GET /api/auth/capabilities` for the authoritative answer
 * whenever possible — these helpers are a synchronous fallback for
 * components that haven't loaded capabilities yet (e.g. on first
 * paint) and as a safety net so we never grant edit access the
 * backend would deny.
 *
 * NEVER use raw role-name comparisons (`user.role === 'manager'`)
 * to gate edit UI. Use `canEditModule(user, moduleKey)` instead.
 */

// Role → set of module keys with default write capability. "*" = all.
const _CAPABILITIES = {
  admin: '*', super_admin: '*', area_manager: '*',
  manager: '*', store_manager: '*',
  supervisor: ['daily_entry', 'store_issues', 'kitchen', 'bar',
               'checklist', 'inventory', 'marketing', 'reservations',
               'customer_experience', 'maintenance', 'petty_cash',
               // ITER293 — new modules added to registry.
               'loyalty', 'customers', 'communication', 'menus',
               // ITER295 — Operations Excellence · Tickets.
               'tickets'],
  accountant: ['pnl', 'reports', 'petty_cash', 'daily_entry'],
  chef: ['recipes', 'production', 'kitchen', 'inventory'],
  production_head: ['recipes', 'production', 'pricing'],
  // ITER308-B · maintenance_manager — mirrors backend CAPABILITIES.
  maintenance_manager: ['maintenance', 'checklist', 'tickets',
                         'compliance', 'notification_rules', 'ticket_settings'],
  cashier: ['daily_entry', 'petty_cash'],
  maintenance: ['maintenance', 'checklist'],
};

const _ADMIN_FAMILY = new Set(['admin', 'super_admin', 'area_manager']);
const _OUTLET_SCOPED = new Set([
  'manager', 'supervisor', 'store_manager', 'chef',
  'accountant', 'cashier', 'maintenance',
]);

function _hasAssignedModule(user, moduleKey) {
  const mods = Array.isArray(user?.assigned_modules) ? user.assigned_modules : [];
  if (user?.role === 'manager' && mods.length === 0) return true; // legacy full access
  return mods.includes(moduleKey);
}

function _outletInScope(user, outletId) {
  if (_ADMIN_FAMILY.has(user?.role)) return true;
  if (!outletId) return true;
  const primary = user?.outlet_id;
  const assigned = new Set(Array.isArray(user?.assigned_outlet_ids) ? user.assigned_outlet_ids : []);
  if (primary) assigned.add(primary);
  if (assigned.size === 0) return true;
  return assigned.has(outletId);
}

/**
 * SINGLE authoritative write-permission gate (frontend mirror).
 * Returns true iff:
 *   • role is admin-family, OR
 *   • module is in user.assigned_modules
 *   • outlet (if provided) is in user's outlet scope
 *   • role has write capability for the module
 */
export function canEditModule(user, moduleKey, outletId = null) {
  if (!user || !moduleKey) return false;
  const role = user.role;
  if (_ADMIN_FAMILY.has(role)) return true;
  if (!_hasAssignedModule(user, moduleKey)) return false;
  if (_OUTLET_SCOPED.has(role) && !_outletInScope(user, outletId)) return false;
  const caps = _CAPABILITIES[role];
  if (caps === '*') return true;
  if (!caps) return false;
  return caps.includes(moduleKey);
}

/** READ-permission gate. Looser than edit. */
export function canViewModule(user, moduleKey) {
  if (!user || !moduleKey) return false;
  if (_ADMIN_FAMILY.has(user.role)) return true;
  // ITER293 — supervisor no longer sees everything by default; visibility is
  // strictly driven by `assigned_modules` (same as every other non-admin role).
  return _hasAssignedModule(user, moduleKey);
}

/** Resolve a complete capability matrix locally (mirror of resolve_capabilities). */
export function resolveCapabilities(user) {
  const role = user?.role || '';
  const modules = {};
  MODULES.forEach((m) => {
    modules[m.id] = {
      view: canViewModule(user, m.id),
      edit: canEditModule(user, m.id),
    };
  });
  return {
    role,
    is_admin: _ADMIN_FAMILY.has(role),
    assigned_modules: Array.isArray(user?.assigned_modules) ? user.assigned_modules : [],
    outlet_id: user?.outlet_id,
    outlet_scoped: _OUTLET_SCOPED.has(role),
    modules,
  };
}

